import { useMemo } from "react";
import _ from "lodash";
import { AvailabilityMapper, WarehouseMapper } from "@nuorder/client-cart-api";
import type { IScope } from "reducers/scope";
import { SchemaRecord } from "records/schema";
import { ICartData } from "reducers/cart-grid-view";
import type { IAvailability, IDeliveryRange, IGridEntry, IOrderData, IOrderCurrency } from "types/cart/shipment-review";
import { getUniqDateRanges } from "components/shipment-review/shipment-review-grid";
import { getLineItemDate } from "components/shipment-review/helpers/inventory";
import { getBulkUnits, isAllocated } from "helpers/legacy-cart/bulk";
import { alignToNow, getStartOfDate, toSafeDate } from "helpers/dates";
import { ILineItem } from "constants/order-types";
import { isOversellHidden } from "helpers/inventory";
import { useDebounce } from "hooks";
import me from "models/me";
import {
    generateGridEntryId,
    getEntryAttributes,
    getEntryPricing,
    getEntryInventoryData,
    getOverorderedSkus,
    getSizeName,
} from "../helpers/gridEntry";
import { Schema, SchemaField } from "types/schema";
import { AllocationType } from "types/cart";
import { IGridEntryCustomization } from "constants/product-customizations/customization";
import type { IProductSize } from "types/product";
import { getCurrencyInfo } from "lib/globalize-util";

import { isProductCustomizationEnabled } from "components/customization-modal/helpers";

export const NO_WAREHOUSE_ID = "_none";

interface IGridData {
    orderData: IOrderData;
    orderSchema: Schema;
    productSchema: Schema;
}

interface IData {
    scope: IScope;
    schemaMap: { [key: string]: SchemaRecord };
    cartData: ICartData;
}

export const useOrderGridData = ({ scope, cartData, schemaMap }: IData): IGridData => {
    const { availability, products, order, inventory, company, locations, replenishments } = cartData || {};
    const { productWarehouses = {}, pricing, showFieldsError } = cartData || {};

    const useBuildOrder = useMemo<boolean>(() => scope?.brand?.hasPreference("build_order"), [scope?.brand]);

    const [productSchema, orderSchema] = useMemo(
        () => ["product", "order"].map((type) => _.find(schemaMap, { type })?.toJS()),
        [schemaMap],
    );

    const currency = useMemo<IOrderCurrency>(
        () => ({
            wholesale: {
                code: order?.currency_code,
                symbol: getCurrencyInfo(order?.currency_code)?.symbol,
                decimalPlaces: 2,
            },
            retail: {
                code: order?.msrp_currency_code ?? order?.currency_code,
                symbol: getCurrencyInfo(order?.msrp_currency_code ?? order?.currency_code)?.symbol,
                decimalPlaces: 2,
            },
        }),
        [order?.currency_code, order?.msrp_currency_code],
    );

    const warehouses = useMemo(
        () => _.map(scope?.brand?.settings?.warehouses, (x) => WarehouseMapper.toDomain(x)),
        [scope?.brand],
    );

    const availabilityData = useMemo<IAvailability>(
        () =>
            _.mapValues(availability, (productsMap) =>
                _.mapValues(productsMap, (data) => AvailabilityMapper.toDomain(data, { warehouses })),
            ),
        [availability, warehouses],
    );

    const lineItemSchemaFields = useMemo<SchemaField[]>(
        () => orderSchema.fields.filter((schemaField: SchemaField) => schemaField.key.startsWith("line_items.")),
        [orderSchema],
    );

    const deliveryConfig = useMemo(
        () =>
            ({
                enforceAvailableUntil: scope?.brand.getPreference("enforce_available_until"), // using only for buildOrder
                minimumDeliveryWindow: scope?.brand.getPreference("minimum_delivery_window"), // using only for buildOrder
                defaultDeliveryWindow: scope?.brand.getPreference("default_delivery_window"), // using only for buildOrder
                dbpPriceEnabled: scope?.brand.hasPreference("date_based_pricing_enabled"),
                cumulativeDate:
                    scope?.brand.getPreference("cumulative_date") === "ship_start" ? "shipStart" : "shipEnd",
                allocateInventoryAllLocationsCombined: scope?.brand.getPreference(
                    "allocate_inventory_all_locations_combined",
                ),
                holdInventory:
                    scope?.brand.getPreference("reserve_inventory_at") === "on_add_to_cart" && me.isBrandUser(),
                hideBulkUnits: scope?.brand.getPreference("hide_bulk_units_entry"),
                allowCustomizations: isProductCustomizationEnabled(scope?.brand, scope?.company),
            }) as const,
        [scope?.brand, scope?.company],
    );

    const shipments = useMemo<IDeliveryRange[]>(
        () =>
            useBuildOrder
                ? _.map(order?.__build_order?.ship_dates, ({ ship_start, ship_end }) => ({
                      shipStart: alignToNow(ship_start), // move dates in the past to the current date
                      shipEnd: toSafeDate(ship_end),
                  }))
                : null,
        [useBuildOrder, order?.__build_order?.ship_dates],
    );

    // Too frequent inventory updates crash AgGrid
    const inventoryDebounced = useDebounce(inventory, 300);

    const productsMap = useMemo(() => _.keyBy(products, "_id"), [products]);

    const orderData = useMemo<IOrderData>(() => {
        if (!order) {
            return null;
        }
        const selectedLocations = company?.allow_bulk ? locations : [null];

        const overorderedSkusSet = getOverorderedSkus(
            replenishments,
            order.line_items,
            productsMap,
            deliveryConfig.cumulativeDate,
        );
        const getInventory = getEntryInventoryData(inventoryDebounced, replenishments, scope.brand);

        const entries: IGridEntry[] = [];
        _.forEach(order?.line_items, (rawLineItem) => {
            const customizationsData = {} as IGridEntryCustomization;
            const product = productsMap[rawLineItem.product];
            const defaultWarehouseId = productWarehouses[rawLineItem.product]?.default ?? null;
            const availableWarehouses = _.compact(
                _.map(productWarehouses[rawLineItem.product]?.available, (id) => _.find(warehouses, { id })),
            );
            const shipStart = toSafeDate(rawLineItem.ship_start); // TODO: extra validation of dates?
            const shipEnd = toSafeDate(rawLineItem.ship_end);
            const cumulativeDate = getStartOfDate(getLineItemDate(rawLineItem, deliveryConfig.cumulativeDate));

            const bulkUnits =
                company?.allow_bulk && !deliveryConfig.hideBulkUnits
                    ? getBulkUnits(rawLineItem, pricing.line_items[rawLineItem._id].bulkUnits)
                    : null;
            const discount = rawLineItem.discount || order.discount || 0;

            const attributes = getEntryAttributes({
                rawLineItem,
                lineItemSchemaFields,
                product,
                checkErrors: showFieldsError,
            });

            if (rawLineItem.__product_customizations_template_id && deliveryConfig.allowCustomizations) {
                customizationsData.customization = {
                    templateId: rawLineItem.__product_customizations_template_id,
                    customizations: rawLineItem.__customizations,
                    orderId: order._id,
                };
            }

            // We need one value always. Empty id means combined location. Null means single shipping address.
            for (const location of selectedLocations) {
                // the same across all sizes for the same location
                const warehouse =
                    (location?.id
                        ? _.find(rawLineItem.sizes, { location: location.id })?.warehouse
                        : rawLineItem.warehouse) || defaultWarehouseId;
                for (const size of product?.sizes ?? []) {
                    const sizePricing =
                        pricing?.line_items[rawLineItem._id]?.sizesByLocations?.[location?.id ?? null]?.[size.size];

                    // `selectedLocation` is used to get size allocation for a particular shipping address.
                    // If `selectedLocation` is falsy, only take line item size which has combined allocation (rawSize.location is falsy)
                    const rawLineItemSize =
                        rawLineItem.sizes.find(
                            (rawSize) =>
                                rawSize.size === size.size &&
                                (location?.id ? rawSize.location === location.id : !rawSize.location),
                        ) ||
                        generateSize(size, {
                            location: location?.id ?? null,
                            warehouse,
                            price: sizePricing?.wholesale ?? 0,
                            originalPrice: sizePricing?.original_price ?? 0,
                        });

                    const entryWarehouseId = rawLineItemSize.warehouse || warehouse;

                    const allocated = company?.allow_bulk ? rawLineItem.allocated || AllocationType.Combined : null;

                    const isEntryAllocated = isAllocated(allocated, location);

                    const entry: IGridEntry = {
                        id: generateGridEntryId({
                            lineItemId: rawLineItem._id,
                            sizeId: rawLineItemSize._id,
                            size: size.size,
                            location: location?.id,
                        }),
                        cartLineItemId: rawLineItem._id,
                        prebook: rawLineItem.prebook,
                        quantity: rawLineItemSize.quantity,
                        // Use materialized units per pack and fallback to source of truth, which is from product size
                        unitsPerPack: rawLineItemSize.__units_per_pack ?? size.__units_per_pack,
                        warehouse: (entryWarehouseId && _.find(warehouses, { id: entryWarehouseId })) || null,
                        available: !!availabilityData[entryWarehouseId ?? NO_WAREHOUSE_ID]?.[rawLineItem.product],
                        shipStart,
                        shipEnd,
                        location,
                        bulkUnits,
                        defaultWarehouseId,
                        availableWarehouses,
                        allocated,
                        pricing: getEntryPricing({ sizePricing, currency, discount }),
                        attributes,
                        hasError: isEntryAllocated && _.some(attributes, "hasError"),
                        inventory: rawLineItem.prebook
                            ? { available: null, displayQuantity: null }
                            : getInventory(entryWarehouseId, product?._id, size._id, cumulativeDate),
                        product,
                        size,
                        ...customizationsData,
                    };

                    // TODO: move it to a separate function and use less dependencies if possible
                    const useReplenishments = replenishments?.[entryWarehouseId]?.[size?._id];
                    if (
                        !entry.quantity ||
                        !entry.available ||
                        !isEntryAllocated ||
                        entry.prebook ||
                        isOversellHidden({ allows_overselling: entry.warehouse?.allowsOverselling }) ||
                        (!useReplenishments && _.isNil(entry.inventory.available))
                    ) {
                        entry.isOverordered = false;
                    } else if (useReplenishments) {
                        entry.isOverordered = overorderedSkusSet.has(`${warehouse}_${size._id}_${cumulativeDate}`);
                    } else {
                        const sizeName = getSizeName(size);

                        // total quantity for the same size and the same warehouse (warehouse could be undefined in case of non-bulk mode)
                        const quantity = _.sumBy(rawLineItem.sizes, ({ size, warehouse, quantity }) =>
                            size === sizeName && warehouse === rawLineItemSize.warehouse ? quantity : 0,
                        );

                        entry.isOverordered = quantity > entry.inventory.available;
                    }

                    entries.push(entry);
                }
            }
        });

        const allShipments = shipments && getUniqDateRanges([...entries, ...shipments]);
        return {
            id: order._id,
            useBulk: company?.allow_bulk,
            uniqueDates: allShipments || getUniqDateRanges(entries), // use entries delivery dates for consistency when no build order
            shipments: allShipments,
            currency,
            entries,
            deliveryConfig,
            selectedLocations,
            availability: availabilityData,
        };
    }, [
        productsMap,
        availabilityData,
        company?.allow_bulk,
        currency,
        deliveryConfig,
        lineItemSchemaFields,
        locations,
        order,
        pricing?.line_items,
        productWarehouses,
        shipments,
        warehouses,
        replenishments,
        inventoryDebounced,
        showFieldsError,
        scope?.brand,
    ]);

    return {
        orderData,
        productSchema,
        orderSchema,
    };
};

const generateSize = (productSize: IProductSize, overrides: Partial<ILineItem>): Partial<ILineItem> =>
    _.defaults(overrides, {
        _id: `placeholderId_${productSize.size}_${overrides.location}`,
        size: `${productSize.size}`,
        size_group: productSize.size_group,
        quantity: 0,
        price: overrides.price,
        originalPrice: overrides.originalPrice,
        __weight: productSize.__weight,
        __units_per_pack: productSize.__units_per_pack || 1,
    });
